import { applyPatch } from 'fast-json-patch';
import { Operation } from 'fast-json-patch/module/core';
import { Observable, Subject, Subscription, filter, map } from 'rxjs';

import { Injectable, inject } from '@angular/core';

import {
  BaseWsService,
  ParsedWebsocketMessage,
  ResourcesService,
  ToastService,
  WebsocketMessageService,
} from '@mitsubishi/core';

import { ProjectFacade } from '../../state/project/project.facade';
import { ProjectStudiesStore } from '../../stores/project-studies.store';
import { isNewStudyPageEnabled } from '../../utils/new-study-page';
import { APPLY_PATCH_TOPIC, ApplyPatchMessages, PATCH_SIGNAL_TOPIC } from './apply-patch.messages';
import { StudyChange, isStudyOp, readStudyChange } from './study-change';

@Injectable({
  providedIn: 'root',
})
export class ApplyPatchWsService extends BaseWsService {
  /** Every patch that arrives, next'ed after it has been applied to the NGXS project tree. */
  readonly #patchOps = new Subject<ReadonlyArray<Operation>>();

  /**
   * With the new study page on, the NGXS tree carries no studies (`?studies=none`) and study ops are never
   * applied to it: with `validate` on they would throw against the empty map and void the whole patch,
   * selections included. They go to `ProjectStudiesStore` as invalidation instead (PAGINATION.md §7).
   */
  readonly #studiesOutsideTree = isNewStudyPageEnabled();
  readonly #projectStudies = inject(ProjectStudiesStore);

  /** The marker subscription that declares this client to the backend; only when the new study page is on. */
  #markerSubscription: Subscription | undefined;

  constructor(
    wsMessageService: WebsocketMessageService,
    serverResourceService: ResourcesService,
    toastService: ToastService,
    private projectFacade: ProjectFacade
  ) {
    super(wsMessageService, serverResourceService, toastService);
  }

  /**
   * The patch stream read as one study's changes. For a consumer that does not read the NGXS project
   * tree — the paginated study page reads its own signal store — a patch is not state, it is the only
   * notice that a mutation landed server-side: the study-level writes that page issues reach no NGXS
   * action. Its arrival is a signal to refetch, never something to apply — the interim realtime regime
   * of `PAGINATION.md` §7.
   *
   * Emitted whether or not the apply to NGXS succeeded: a patch that could not be applied still means
   * the server changed, which is the whole of the signal.
   */
  studyChanges$(studyId: string): Observable<StudyChange> {
    return this.#patchOps.pipe(
      map(ops => readStudyChange(ops, studyId)),
      filter((change): change is StudyChange => change !== null)
    );
  }

  subscribeTopic(projectId: string) {
    super.subscribeTopic(projectId);
    // Declaring the client: the backend's JsonDiffAspect sends no study diff to users subscribed here
    // (PLAN.md §4 P3, relief lever); they follow the study events instead, on the project topic
    // (`ProjectWsService` → `ProjectStudiesStore.onStudyEvent`) and on the study page's own topic.
    if (this.#studiesOutsideTree) {
      this.#markerSubscription = this.wsMessageService.watchTopic(`${PATCH_SIGNAL_TOPIC}/${projectId}`).subscribe();
    }
  }

  override unsubscribeTopic(): void {
    this.#markerSubscription?.unsubscribe();
    this.#markerSubscription = undefined;
    super.unsubscribeTopic();
  }

  protected get excludedMessagesFromLog(): string[] {
    return [];
  }

  protected setTopic(projectId: string) {
    this.topic = `${APPLY_PATCH_TOPIC}/${projectId}`;
  }

  protected handleMessage(message: ParsedWebsocketMessage) {
    super.handleMessage(message);

    const payload = message.payload;
    switch (message.description) {
      case ApplyPatchMessages.ApplyPatch: {
        const patch: ReadonlyArray<Operation> = JSON.parse(payload.patch);
        const studyOps: Operation[] = [];
        const applicable: Operation[] = [];
        for (const op of patch) {
          (this.#studiesOutsideTree && isStudyOp(op) ? studyOps : applicable).push(op);
        }
        const currentProject = this.projectFacade.currentProject;
        try {
          const patchedProject = applyPatch(currentProject, applicable, true, false);
          this.projectFacade.setProject(patchedProject.newDocument);
        } catch (error) {
          console.error('ERROR PATCH:', applicable);
          console.log(error);
        }
        if (studyOps.length) {
          this.#projectStudies.onStudyOps(studyOps);
        }
        this.#patchOps.next(patch);
        break;
      }
    }
  }
}
